import {
  MessagePrimitive,
  useAssistantState,
  useMessage,
  useMessagePartText,
} from "@assistant-ui/react";
import { Box, Flex, Icon, keyframes, Spinner, Text } from "@chakra-ui/react";
import React, { ReactElement, useRef } from "react";
import { HiOutlineCheckCircle } from "react-icons/hi2";

import { PrettyMarkdown } from "../../../../components";
import { useCustomInsightsChat } from "./CustomInsightsChatProvider";
import {
  isSetFiltersMessage,
  stripSkillPrefix,
  TOOL_LABELS,
} from "./customInsightsChatUtils";
import { CustomInsightsReportPrettyMarkdown } from "./CustomInsightsReportPrettyMarkdown";
import CustomInsightsResultCard from "./CustomInsightsResultCard";
import { ResultActions } from "./ResultActions";

function Reasoning({ text }: { text: string }): ReactElement {
  return (
    <Text
      fontSize="xs"
      color="gray.400"
      whiteSpace="pre-wrap"
      px={4}
      py="2px"
      data-copy-role="reasoning"
    >
      {text}
    </Text>
  );
}

const TOOL_UI_HANDLED = new Set(["render_chart"]);
const TOOL_STATUS_HIDDEN = new Set([
  "manage_todos",
  "code_execution",
  "web_search",
]);
const STATUS_TEXT_COLOR = "#67717E";
const STATUS_SUCCESS_COLOR = "#259B71";
const THINKING_LABEL = "Thinking";

const loadingEllipsis = keyframes`
  to {
    width: 1.25em;
  }
`;

export function LoadingStatusLabel({ label }: { label: string }): ReactElement {
  return (
    <Flex alignItems="center" minW={0}>
      <Text
        fontSize="12px"
        lineHeight="16px"
        fontWeight="semibold"
        color={STATUS_TEXT_COLOR}
      >
        {label}
      </Text>
      <Box width="1.25em" color={STATUS_TEXT_COLOR}>
        <Box
          animation={`${loadingEllipsis} steps(4,end) 2s infinite`}
          overflow="hidden"
          whiteSpace="nowrap"
          width="0"
        >
          ...
        </Box>
      </Box>
    </Flex>
  );
}

function CompletedStatusLabel({ label }: { label: string }): ReactElement {
  return (
    <Flex alignItems="center" gap="6px">
      <Icon
        as={HiOutlineCheckCircle}
        boxSize="16px"
        color={STATUS_SUCCESS_COLOR}
        flexShrink={0}
      />
      <Text fontSize="12px" lineHeight="16px" color={STATUS_TEXT_COLOR}>
        {label}
      </Text>
    </Flex>
  );
}

type ToolPart = {
  type: string;
  toolName?: string;
  toolCallId?: string;
  status?: { type?: string };
};

function getToolLabel(part: ToolPart): string {
  const name = part.toolName ?? "";
  return TOOL_LABELS[name] ?? name;
}

function isVisibleToolCall(part: ToolPart): boolean {
  if (part.type !== "tool-call") return false;
  const name = part.toolName ?? "";
  return !TOOL_STATUS_HIDDEN.has(name) && !TOOL_UI_HANDLED.has(name);
}

function ToolFallback({
  toolName,
  toolCallId,
  status,
}: {
  toolName: string;
  toolCallId: string;
  status: { type: string };
}): ReactElement | null {
  const parts = useAssistantState((s) => s.message.parts);
  const messageStatus = useAssistantState((s) => s.message.status.type);
  const allParts = parts as ToolPart[];

  if (TOOL_UI_HANDLED.has(toolName)) return null;
  if (TOOL_STATUS_HIDDEN.has(toolName)) return null;

  const toolParts = allParts.filter(isVisibleToolCall);
  const toolIndex = toolParts.findIndex((p) => p.toolCallId === toolCallId);
  const label = TOOL_LABELS[toolName] ?? toolName;

  if (toolIndex === -1) {
    // Only show a spinner while this part is actively running. Any terminal
    // state (complete, incomplete, requires-action) finalizes to a checkmark —
    // otherwise a stalled / cancelled / errored run leaves a spinner forever.
    const isLoading = status.type === "running";
    return (
      <Flex
        alignItems="center"
        gap="6px"
        px={4}
        py="2px"
        data-copy-role="tool-status"
      >
        {isLoading ? (
          <>
            <Spinner size="xs" color="purple.400" flexShrink={0} />
            <LoadingStatusLabel label={label} />
          </>
        ) : (
          <CompletedStatusLabel label={label} />
        )}
      </Flex>
    );
  }

  // Render each label once, at its first occurrence. Later occurrences of the
  // same label (consecutive or not) are suppressed — this prevents a "restart"
  // visual where a completed step reverts to loading when the agent re-runs it.
  // Ongoing work during a re-run is covered by ThinkingIndicator.
  const firstLabelIndex = toolParts.findIndex((p) => getToolLabel(p) === label);
  if (firstLabelIndex !== toolIndex) return null;

  // Monotonic completion: once ANY occurrence of this label is complete, stay
  // complete (green check) even if a later occurrence is running.
  const anyComplete = toolParts.some(
    (p) => getToolLabel(p) === label && p.status?.type === "complete"
  );
  // Only show a spinner while the message is actively running. If the run
  // finishes in any terminal state (complete / incomplete / requires-action),
  // finalize the step to a checkmark — otherwise a cancelled or errored run
  // leaves the tool-call part with an inherited non-complete status and the
  // spinner would be stuck forever (the "Searching interviews never finishes"
  // bug).
  const showSpinner = !anyComplete && messageStatus === "running";

  return (
    <Flex
      alignItems="center"
      gap="6px"
      px={4}
      py="2px"
      data-copy-role="tool-status"
    >
      {showSpinner ? (
        <>
          <Spinner size="xs" color="purple.400" flexShrink={0} />
          <LoadingStatusLabel label={label} />
        </>
      ) : (
        <CompletedStatusLabel label={label} />
      )}
    </Flex>
  );
}

function ThinkingIndicator(): ReactElement | null {
  const statusType = useAssistantState((s) => s.message.status.type);
  const parts = useAssistantState((s) => s.message.parts) as ToolPart[];

  if (statusType !== "running") return null;

  const lastPart = parts[parts.length - 1];
  if (lastPart && (lastPart.type === "text" || lastPart.type === "reasoning")) {
    return null;
  }

  let lastVisible: ToolPart | undefined;
  for (let i = parts.length - 1; i >= 0; i--) {
    if (isVisibleToolCall(parts[i])) {
      lastVisible = parts[i];
      break;
    }
  }

  if (lastVisible && lastVisible.status?.type !== "complete") {
    // Last visible step is running. If its label has already completed earlier
    // in this message, ToolFallback has deduped it (no spinner is on screen for
    // it) — fill the gap with Thinking. Otherwise ToolFallback is rendering the
    // step's own spinner, so Thinking would be redundant.
    const lastVisibleLabel = getToolLabel(lastVisible);
    const hasEarlierComplete = parts.some(
      (p) =>
        isVisibleToolCall(p) &&
        p.status?.type === "complete" &&
        getToolLabel(p) === lastVisibleLabel
    );
    if (!hasEarlierComplete) return null;
  }

  return (
    <Flex
      alignItems="center"
      gap="6px"
      px={4}
      py="2px"
      data-copy-role="thinking"
    >
      <Spinner size="xs" color="purple.400" flexShrink={0} />
      <LoadingStatusLabel label={THINKING_LABEL} />
    </Flex>
  );
}

function AssistantLoadingIndicator(): ReactElement | null {
  const statusType = useAssistantState((s) => s.message.status.type);

  if (statusType === "incomplete" || statusType === "error") {
    return (
      <Flex
        px={4}
        py={3}
        fontSize="sm"
        textColor="red.600"
        alignItems="center"
        gap={2}
        width="100%"
        bg="red.50"
        borderBottomRadius="lg"
      >
        <Text>Something went wrong. Please try again.</Text>
      </Flex>
    );
  }

  return null;
}

function AssistantTextBubble(): ReactElement {
  const { text } = useMessagePartText();
  return <AssistantTextBubbleRaw text={text} />;
}

function AssistantReportTextBubble(): ReactElement {
  const { text } = useMessagePartText();
  return <AssistantTextBubbleRaw text={text} isReport />;
}

export function AssistantTextBubbleRaw({
  text,
  isReport = false,
}: {
  text: string;
  isReport?: boolean;
}): ReactElement {
  return (
    <Box
      px={isReport ? 0 : 4}
      py={isReport ? 0 : 2}
      fontSize={isReport ? "md" : "sm"}
      textColor="gray.800"
      width="100%"
      mt={isReport ? 0 : 1}
      data-copy-role="text-bubble"
    >
      <Box flex={1} minW={0} overflowX="auto">
        {isReport ? (
          <CustomInsightsReportPrettyMarkdown>
            {text}
          </CustomInsightsReportPrettyMarkdown>
        ) : (
          <PrettyMarkdown>{text}</PrettyMarkdown>
        )}
      </Box>
    </Box>
  );
}

function UserText(): ReactElement {
  const { text } = useMessagePartText();
  return (
    <Flex
      background="gray.500"
      color="white"
      padding="8px 12px"
      borderRadius="md"
    >
      {stripSkillPrefix(text)}
    </Flex>
  );
}

export function UserMessage(): React.ReactElement | null {
  const content = useMessage((state) => state.content);
  if (isSetFiltersMessage(content)) {
    return null;
  }

  return (
    <Flex
      mt={4}
      p={3}
      px={4}
      fontSize="sm"
      fontWeight="500"
      textColor="gray.800"
      whiteSpace="pre-wrap"
      width="100%"
      justifyContent="end"
    >
      <MessagePrimitive.Parts components={{ Text: UserText }} />
    </Flex>
  );
}

export function AssistantMessage(): ReactElement {
  const messageId = useAssistantState((s) => s.message.id);
  const statusType = useAssistantState((s) => s.message.status.type);
  const { messageIdsWithCharts } = useCustomInsightsChat();
  const hasCharts = messageIdsWithCharts.has(messageId);
  const contentRef = useRef<HTMLDivElement>(null);
  const isComplete = statusType === "complete";
  const showResultActions = isComplete && hasCharts && !!messageId;
  const isCompletedInsightsReport = isComplete && hasCharts;

  const messageParts = (
    <MessagePrimitive.Parts
      unstable_showEmptyOnNonTextEnd={false}
      components={{
        Empty: () => null,
        Text: isCompletedInsightsReport
          ? AssistantReportTextBubble
          : AssistantTextBubble,
        Reasoning: isCompletedInsightsReport ? () => null : Reasoning,
        tools: {
          by_name: { manage_todos: () => null }, // eslint-disable-line camelcase
          Fallback: isCompletedInsightsReport ? () => null : ToolFallback,
        },
      }}
    />
  );

  return isCompletedInsightsReport ? (
    <Box mt={4} width="100%">
      <CustomInsightsResultCard
        mt={0}
        bodyRef={contentRef}
        footer={
          showResultActions ? (
            <ResultActions messageId={messageId} contentRef={contentRef} />
          ) : undefined
        }
      >
        {messageParts}
      </CustomInsightsResultCard>
    </Box>
  ) : (
    <Box mt={4} width="100%">
      <Box ref={contentRef}>{messageParts}</Box>
      <ThinkingIndicator />
      <AssistantLoadingIndicator />
      {showResultActions && (
        <ResultActions messageId={messageId} contentRef={contentRef} />
      )}
    </Box>
  );
}
